// "Show me this stretch of time" — the encoding shared by the Layers panel's
// two time controls (damage markers and FIRMS hotspots).
//
// A stored window is a single string, so it survives a round trip through
// localStorage and a <select> value without a parser on either side:
//
//   ''                          everything the source holds
//   'h:24'                      the last 24 hours, sliding
//   'd:7'                       the last 7 days, sliding
//   'c:2026-06-01:2026-06-05'   a fixed range; either date may be blank for an
//                               open edge ('c:2026-06-01:' = since June 1)
//
// The distinction that matters downstream is `toMs === null`: a sliding window
// ends at "now" and moves, a fixed one is pinned to the past. FIRMS turns that
// into the tile's `until` parameter (migration 027) rather than a client-side
// filter, because its tiles are decimated to one detection per cell and a
// narrowing applied after the fact would drop the wrong ones.

export interface TimeWindow {
  /** Epoch ms, or null for an open start (as far back as the source goes). */
  fromMs: number | null;
  /** Epoch ms, or null for "now" — a live window that slides forward. */
  toMs: number | null;
}

/** Local midnight for a 'YYYY-MM-DD' date. Deliberately NOT `new Date(s)`,
 *  which reads a bare date as UTC and shifts the edge by the viewer's offset —
 *  picking "June 1" would then include or drop part of a neighbouring day. */
export function dayStartMs(iso: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  if (!m) return null;
  const ms = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).getTime();
  return Number.isNaN(ms) ? null : ms;
}

/** Bring a stored spec up to date so everything downstream sees one format.
 *  The only legacy form is the damage window's bare day count ('3'), which
 *  has to be folded in before the preset <select> looks for its option — an
 *  unmatched value would silently present as a custom range. */
export function normalizeWindow(spec: string | null | undefined): string {
  if (!spec) return '';
  return /^\d+$/.test(spec) ? `d:${spec}` : spec;
}

/** Resolve a stored window against the current clock. The `to` edge of a fixed
 *  range is the END of that day, so "June 1 to June 1" is all of June 1. */
export function resolveWindow(spec: string): TimeWindow {
  if (spec.startsWith('c:')) {
    const [, from = '', to = ''] = spec.split(':');
    const toStart = dayStartMs(to);
    return {
      fromMs: dayStartMs(from),
      toMs: toStart === null ? null : toStart + 86_400_000 - 1,
    };
  }
  const m = /^([hd]):(\d+(?:\.\d+)?)$/.exec(normalizeWindow(spec));
  if (!m) return { fromMs: null, toMs: null };
  const span = Number(m[2]) * (m[1] === 'h' ? 3_600_000 : 86_400_000);
  return { fromMs: Date.now() - span, toMs: null };
}

/** The 'YYYY-MM-DD' a date input wants, in LOCAL terms to match dayStartMs. */
export function isoDay(ms: number | null): string {
  if (ms === null) return '';
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
    + `-${String(d.getDate()).padStart(2, '0')}`;
}
